import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { navigate } from "../../lib/router";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";
import { ChevronLeft, Plus, Save, Trash2, Building2, TrendingUp, Loader2 } from "lucide-react";
import { SectionCard } from "../../components/SectionCard";
import { toast } from "sonner";
import { NewRoomDrawer } from "./components/NewRoomDrawer";
import { NewSpecialtyDrawer } from "./components/NewSpecialtyDrawer";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import { cn } from "../../lib/utils";

interface Specialty {
  id: number;
  name: string;
  room_id?: number | null;
}

interface Room {
  id: number;
  name: string;
  capacity: number;
}

export default function ResourcesPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].resources;
  const tRooms = settingsLabels[lang].rooms;
  const tSpecs = settingsLabels[lang].specialties;

  const [activeTab, setActiveTab] = useState<"rooms" | "specialties">("rooms");
  const [rooms, setRooms] = useState<Room[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRoomDrawerOpen, setIsRoomDrawerOpen] = useState(false);
  const [isSpecDrawerOpen, setIsSpecDrawerOpen] = useState(false);

  // Pricing state: { specialtyId: { frequency: priceInCents } }
  const [prices, setPrices] = useState<Record<string, Record<number, string>>>({});

  const load = async () => {
    setLoading(true);
    const [roomsRes, specsRes] = await Promise.all([
      api.listCompanyRooms(),
      api.listCompanySpecialties(),
    ]);

    if (roomsRes.ok) setRooms(roomsRes.data);
    if (specsRes.ok) setSpecialties(Array.isArray(specsRes.data) ? specsRes.data : []);

    const savedPrices = localStorage.getItem("specialty_pricing");
    if (savedPrices) {
      try {
        setPrices(JSON.parse(savedPrices));
      } catch (e) {
        console.error("Failed to parse specialty pricing", e);
      }
    } else {
      setPrices({
        "default": { 1: "198,00", 2: "330,00", 3: "450,00", 4: "550,00" }
      });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handlePriceChange = (specId: string, freq: number, value: string) => {
    setPrices(prev => ({
      ...prev,
      [specId]: {
        ...(prev[specId] || prev["default"] || {}),
        [freq]: value
      }
    }));
  };

  const savePrices = () => {
    localStorage.setItem("specialty_pricing", JSON.stringify(prices));
    toast.success(t.pricingSaved);
  };

  const deleteRoom = async (id: number) => {
    if (!confirm(t.confirmDeleteRoom)) return;
    const res = await api.deleteCompanyRoom(id);
    if (res.ok) {
      toast.success(t.roomDeleted);
      load();
    }
  };

  const deleteSpecialty = async (id: number) => {
    if (!confirm(t.confirmDeleteSpecialty)) return;
    const res = await api.deleteCompanySpecialty(id);
    if (res.ok) {
      toast.success(t.specialtyDeleted);
      load();
    }
  };

  const updateRoom = async (specId: number, roomId: string) => {
    const rid = roomId === "none" ? null : Number(roomId);
    const res = await api.updateCompanySpecialty(specId, { room_id: rid });
    if (res.ok) {
      toast.success(t.roomUpdated);
      setSpecialties(prev => prev.map(item => item.id === specId ? { ...item, room_id: rid } : item));
    } else {
      toast.error(res.error || t.updateRoomError);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl p-4 md:p-6 space-y-8 animate-in fade-in duration-500">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/app/settings")}
            className="rounded-xl hover:bg-neutral-100"
          >
            <ChevronLeft className="h-6 w-6 text-neutral-600" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">
              {t.title}
            </h1>
            <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
              {t.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Segmented Control */}
          <div className="flex p-1 bg-neutral-100 rounded-2xl w-fit shadow-inner">
            <button 
              onClick={() => setActiveTab("rooms")}
              className={cn(
                "px-6 py-2 rounded-xl text-[10px] font-bold transition-all uppercase tracking-widest",
                activeTab === "rooms" ? "bg-white text-neutral-900" : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              {t.tabs.rooms}
            </button>
            <button 
              onClick={() => setActiveTab("specialties")}
              className={cn(
                "px-6 py-2 rounded-xl text-[10px] font-bold transition-all uppercase tracking-widest",
                activeTab === "specialties" ? "bg-white text-neutral-900" : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              {t.tabs.specialties}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-20 flex flex-col items-center gap-4 text-center">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">
            {t.loading}
          </p>
        </div>
      ) : (
        <div className="w-full">
          {activeTab === "rooms" ? (
            <div className="space-y-6 animate-in slide-in-from-left-4 duration-500">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-neutral-800">{tRooms.header}</span>
                  <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    {rooms.length} {t.registered}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setIsRoomDrawerOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-10 px-4 rounded-2xl transition-all"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {tRooms.new}
                </Button>
              </div>
              
              {rooms.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-3xl border-neutral-100 text-neutral-400 bg-neutral-50/30">
                  <p className="text-xs font-bold uppercase tracking-widest">{tRooms.none}</p>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden divide-y divide-neutral-50">
                  {rooms.map(room => (
                    <div key={room.id} className="group flex items-center justify-between px-6 py-4 hover:bg-neutral-50/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-neutral-50 flex items-center justify-center text-neutral-400 group-hover:bg-white transition-colors border border-neutral-100">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-bold text-neutral-800 text-sm">{room.name}</div>
                          <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                            {tRooms.capacitySuffix.replace("{n}", String(room.capacity))}
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => deleteRoom(room.id)} 
                        className="text-neutral-300 hover:text-destructive hover:bg-destructive/10 rounded-xl transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-neutral-800">{tSpecs.header}</span>
                  <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    {specialties.length} {t.registered}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setIsSpecDrawerOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-10 px-4 rounded-2xl transition-all"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {tSpecs.new}
                </Button>
              </div>

              {specialties.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-3xl border-neutral-100 text-neutral-400 bg-neutral-50/30">
                  <p className="text-xs font-bold uppercase tracking-widest">{tSpecs.none}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {specialties.map((spec) => (
                    <SectionCard 
                      key={spec.id}
                      header={
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-bold text-neutral-800">{spec.name}</span>
                        </div>
                      }
                      action={
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => deleteSpecialty(spec.id)} 
                          className="text-neutral-300 hover:text-destructive hover:bg-destructive/10 rounded-xl transition-all h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      }
                    >
                      <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                              {lang === 'pt' ? "Sala Padrão" : "Default Room"}
                            </Label>
                            <div className="relative">
                              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
                              <Select 
                                value={spec.room_id ? String(spec.room_id) : "none"} 
                                onValueChange={(val) => updateRoom(spec.id, val)}
                              >
                                <SelectTrigger className="pl-11 rounded-2xl">
                                  <SelectValue placeholder={tSpecs.newPage.fields.noneSelected} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">{tSpecs.newPage.fields.noneSelected}</SelectItem>
                                  {rooms.map(r => (
                                    <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100/50 self-end h-12">
                            <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                              <TrendingUp className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-emerald-900">{t.suggestedPricing}</p>
                              <p className="text-[9px] font-bold text-emerald-600/70 uppercase tracking-widest">{t.valueConfig}</p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {[1, 2, 3, 4].map(freq => (
                            <div key={freq} className="space-y-2">
                              <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 block">
                                {freq}x {t.perWeek}
                              </Label>
                              <div className="relative group">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 font-bold text-xs transition-colors group-focus-within:text-emerald-600">R$</span>
                                <Input 
                                  value={prices[spec.id]?.[freq] || prices["default"]?.[freq] || ""}
                                  onChange={(e) => handlePriceChange(String(spec.id), freq, e.target.value)}
                                  className="pl-10 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 text-sm font-medium transition-all focus:bg-white"
                                  placeholder="0,00"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </SectionCard>
                  ))}

                  <div className="flex justify-end pt-6">
                    <Button 
                      onClick={savePrices} 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 px-8 rounded-2xl transition-all active:scale-95"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {t.savePricing}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <NewRoomDrawer 
        open={isRoomDrawerOpen} 
        onOpenChange={setIsRoomDrawerOpen} 
        onSuccess={load}
      />
      <NewSpecialtyDrawer 
        open={isSpecDrawerOpen} 
        onOpenChange={setIsSpecDrawerOpen} 
        onSuccess={load}
      />
    </div>
  );
}
